import React from 'react';
import { Box, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import { HorizontalCarousel, SizingMultiplier, Typography } from '@ocx/ui';
import { Coordinates } from '../../modules/stores/types';
import { messages } from './offer-details.messages';
import { OfferLocationsCarouselItem } from './OfferLocationsCarouselItem';
import { OfferLocationsCarouselItemOnClickParams } from './types';

export type OfferDetailsAvailableAtLocation = {
  uuid: string;
  name: string;
  address: string;
  coordinates: Coordinates;
};

export type OfferDetailsLocationsCarouselProps = {
  locations?: OfferDetailsAvailableAtLocation[];
  // Indicates if the offer has location restrictions.
  // The `locations` array lists available locations, but restrictions may still apply (e.g., "Location excludes" in Command Center can make this array empty).
  hasLocationConstraints?: boolean;
  memberHasSharedLocation?: boolean;
  onStoreIconClick?: (params: OfferLocationsCarouselItemOnClickParams) => void;
};

export const OfferDetailsLocationsCarousel = ({
  locations,
  hasLocationConstraints,
  memberHasSharedLocation,
  onStoreIconClick,
}: OfferDetailsLocationsCarouselProps) => {
  const theme = useTheme();
  const intl = useIntl();
  let title = '';

  if (!hasLocationConstraints) {
    title = intl.formatMessage(messages['offer-details:location-constraint:title:available-at-all-stores']);
  } else if (hasLocationConstraints && !memberHasSharedLocation) {
    title = intl.formatMessage(
      messages['offer-details:location-constraint:title:may-not-be-available-at-nearby-stores'],
    );
  } else if (hasLocationConstraints && memberHasSharedLocation && locations?.length) {
    title = intl.formatMessage(messages['offer-details:location-constraint:title:available-at-x-locations'], {
      numberOfStores: locations.length,
    });
  }

  if (!title && !locations?.length) {
    return null;
  }

  return (
    <Box>
      <Box position="relative" marginLeft={-SizingMultiplier.lg} marginRight={-SizingMultiplier.lg}>
        {locations?.length ? (
          <Box position="absolute" left={0} top={`calc(26px + ${theme.spacing(SizingMultiplier.md)})`} width="100%">
            <HorizontalCarousel>
              {locations.map(({ uuid, name, address, coordinates }, index) => {
                const isFirstItem = index === 0;
                const isLastItem = index === locations.length - 1;
                const hasMultipleItems = locations.length > 1;

                return (
                  <OfferLocationsCarouselItem
                    key={uuid}
                    uuid={uuid}
                    isFirst={isFirstItem}
                    isLast={isLastItem}
                    hasMultipleItems={hasMultipleItems}
                    name={name}
                    address={address}
                    coordinates={coordinates}
                    onClick={onStoreIconClick}
                  />
                );
              })}
            </HorizontalCarousel>
          </Box>
        ) : undefined}
      </Box>

      <Box borderRadius={SizingMultiplier.md} p={SizingMultiplier.md} bgcolor="background.box">
        {title ? (
          <Typography variant="body2" paddingBottom={locations?.length ? '82px' : undefined}>
            {title}
          </Typography>
        ) : undefined}
      </Box>
    </Box>
  );
};
